import type { FlightCardData } from '@massilia/queries'

import { Button, cn } from '@massilia/ui'
import { RotateCcw } from 'lucide-react'
import { useMemo } from 'react'

import { type AirlineOption, CompanyFilter } from './company-filter'
import { DurationFilter } from './duration-filter'
import { RESET_FILTERS_PATCH } from './filter-url-params'
import { getDurationBounds, getFilterAirline, getPriceBounds } from './filter-utils'
import { useFiltersState } from './filters-state-context'
import { LuggageFilter } from './luggage-filter'
import { PaymentFilter } from './payment-filter'
import { PriceFilter } from './price-filter'
import { StopsFilter } from './stops-filter'
import { TimeFilter } from './time-filter'
import { useHasActiveFilters } from './use-has-active-filters'

interface FlightFiltersProps {
  offers: FlightCardData[]
  /**
   * `card` : la sidebar autonome (encadré, titre, bouton « Réinitialiser »).
   * `plain` : la modale mobile, dont l'en-tête porte déjà le titre « Filtres »
   * et dont le pied collant porte le bouton de réinitialisation.
   */
  variant?: 'card' | 'plain'
}

export function FlightFilters({ offers, variant = 'card' }: FlightFiltersProps) {
  const { setFilters } = useFiltersState()
  const isCard = variant === 'card'

  const priceBounds = useMemo(() => getPriceBounds(offers), [offers])
  const durationBounds = useMemo(() => getDurationBounds(offers), [offers])
  const availableAirlines = useMemo((): AirlineOption[] => {
    const airlinesMap = new Map<string, string>()
    for (const offer of offers) {
      const { name, code } = getFilterAirline(offer)
      if (code && !airlinesMap.has(code)) {
        airlinesMap.set(code, name)
      }
    }
    return Array.from(airlinesMap.entries())
      .map(([code, name]) => ({ name, code }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [offers])

  const hasActiveFilters = useHasActiveFilters(offers)

  return (
    <div className={cn(isCard && 'rounded-lg border border-gray-200 bg-white p-4')}>
      {isCard && <h2 className="mb-4 text-lg font-semibold text-gray-900">Filtres</h2>}

      <div className="space-y-0">
        <CompanyFilter airlines={availableAirlines} offers={offers} />
        <LuggageFilter />
        <PaymentFilter />
        <StopsFilter />
        <PriceFilter bounds={priceBounds} />
        <DurationFilter bounds={durationBounds} />
        <TimeFilter offers={offers} />
      </div>

      {isCard && (
        <div className="flex flex-row justify-between border-t border-gray-200 pt-4">
          <Button
            className="w-full"
            disabled={!hasActiveFilters}
            onClick={() => {
              setFilters(RESET_FILTERS_PATCH)
            }}
            variant="outline"
          >
            <RotateCcw className="ml-2 h-4 w-4" />
            <span className="mr-2 py-2 text-sm font-medium text-wrap text-gray-900">
              Réinitialiser les filtres
            </span>
          </Button>
        </div>
      )}
    </div>
  )
}
